import React, { useEffect, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Avatar,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { getInitials } from './SharedComponents';

const ROLES   = ['Admin', 'Regular', 'Tourist'];
const VENDORS = ['Ericsson', 'Nokia', 'Both'];
const ROLE_LABELS = {
    Admin:   'Admin / Super user',
    Regular: 'Regular user',
    Tourist: 'Tourist / Lurker',
};

const PrivilegeDialog = ({
    open,
    aspNetUsers,
    modules,
    usedUserIds,
    editingPrivilege,
    saving,
    error,
    onClose,
    onSave,
    onClearError,
}) => {
    const isEdit = Boolean(editingPrivilege);

    const [selectedUser, setSelectedUser] = useState(null);
    const [role,         setRole]         = useState('Regular');
    const [vendor,       setVendor]       = useState('Ericsson');
    const [moduleAccess, setModuleAccess] = useState({});

    useEffect(() => {
        if (!open) return;

        if (isEdit) {
            const u = aspNetUsers.find((u) => u.id === editingPrivilege.userId);
            setSelectedUser(u || null);
            setRole(editingPrivilege.role);
            setVendor(editingPrivilege.vendor);
            const map = {};
            (editingPrivilege.moduleAccess || []).forEach((ma) => { map[ma.moduleId] = ma.hasAccess; });
            setModuleAccess(map);
        } else {
            setSelectedUser(null);
            setRole('Regular');
            setVendor('Ericsson');
            const defaultMap = {};
            modules.forEach((m) => { defaultMap[m.id] = true; });
            setModuleAccess(defaultMap);
        }
    }, [open, isEdit, editingPrivilege, aspNetUsers, modules]);

    // Only show users without a privilege assigned (except the one being edited)
    const availableUsers = aspNetUsers.filter(
        (u) => !usedUserIds.includes(u.id) || (isEdit && u.id === editingPrivilege?.userId)
    );

    const handleSave = () => {
        if (!selectedUser) return;
        const payload = {
            ...(isEdit ? {} : { userId: selectedUser.id }),
            role,
            vendor,
            moduleAccess: modules.map((m) => ({ moduleId: m.id, hasAccess: !!moduleAccess[m.id] })),
        };
        onSave(isEdit ? editingPrivilege.userId : null, payload, isEdit);
    };

    const toggleModule = (id) =>
        setModuleAccess((prev) => ({ ...prev, [id]: !prev[id] }));

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
        >
            <DialogTitle sx={{ fontWeight: 500, pb: 1 }}>
                {isEdit ? 'Edit privilege' : 'Assign privilege'}
            </DialogTitle>
            <Divider />

            <DialogContent sx={{ pt: 2.5 }}>
                <Stack spacing={2.5}>
                    {error && (
                        <Alert severity="error" onClose={onClearError}>{error}</Alert>
                    )}

                    {/* User picker — disabled in edit mode */}
                    <Autocomplete
                        options={availableUsers}
                        getOptionLabel={(u) => u.userName || u.email}
                        value={selectedUser}
                        onChange={(_, val) => setSelectedUser(val)}
                        disabled={isEdit}
                        renderOption={(props, u) => (
                            <Box component="li" {...props}>
                                <Avatar sx={{ width: 28, height: 28, fontSize: 11, mr: 1.5, bgcolor: '#E3F2FD', color: '#1565C0' }}>
                                    {getInitials(u.userName)}
                                </Avatar>
                                <Box>
                                    <Typography variant="body2" fontWeight={500}>{u.userName}</Typography>
                                    <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                                </Box>
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="User (AspNetUsers)"
                                size="small"
                                required
                                helperText={
                                    isEdit
                                        ? 'User cannot be changed in edit mode'
                                        : 'Only users without a privilege assigned are shown'
                                }
                            />
                        )}
                    />

                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Role</InputLabel>
                                <Select value={role} label="Role" onChange={(e) => setRole(e.target.value)}>
                                    {ROLES.map((r) => (
                                        <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Vendor</InputLabel>
                                <Select value={vendor} label="Vendor" onChange={(e) => setVendor(e.target.value)}>
                                    {VENDORS.map((v) => (
                                        <MenuItem key={v} value={v}>{v}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Box>
                        <FormLabel component="legend" sx={{ fontSize: 13, mb: 0.5 }}>
                            Module access
                        </FormLabel>
                        <FormGroup row>
                            {modules.map((m) => (
                                <FormControlLabel
                                    key={m.id}
                                    sx={{ width: '50%' }}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={!!moduleAccess[m.id]}
                                            onChange={() => toggleModule(m.id)}
                                        />
                                    }
                                    label={<Typography variant="body2">{m.label}</Typography>}
                                />
                            ))}
                        </FormGroup>
                    </Box>
                </Stack>
            </DialogContent>

            <Divider />
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} variant="outlined" size="small" disabled={saving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    size="small"
                    disableElevation
                    disabled={!selectedUser || saving}
                    startIcon={saving ? <CircularProgress size={14} /> : null}
                >
                    {saving ? 'Saving...' : 'Save privilege'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PrivilegeDialog;